/* eslint-disable */
// eslint has a bug around false positives in for loops
// @flow
import React from 'react';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js';
import { 
  compact as _compact, 
  each as _each, 
  filter as _filter, 
  flatMapDeep as _flatMapDeep, 
  get as _get, 
  groupBy as _groupBy, 
  isEmpty as _isEmpty, 
  isArray as _isArray, 
  map as _map, 
  pick as _pick, 
  reduce as _reduce, 
  snakeCase as _snakeCase, 
  split as _split, 
  toPairs as _toPairs 
} from 'lodash-es';

import _λcompact from 'lodash/fp/compact';
import _λflow from 'lodash/fp/flow';
import _λfromPairs from 'lodash/fp/fromPairs';
import _λgroupBy from 'lodash/fp/groupBy';
import _λmap from 'lodash/fp/map';
import _λreplace from 'lodash/fp/replace';
import _λsortBy from 'lodash/fp/sortBy';
import _λsplit from 'lodash/fp/split';

import { config as apiConfig } from 'api';

import emailValidator from 'email-validator';

import TextMuted from 'components/shared/TextMuted'; // madness


/* ======================================== */
/*                 GENERAL                  */
/* ======================================== */

const sorter = {
  date: key => (a, b) => moment(_get(a, key)).diff(moment(_get(b, key))),
  number: key => (a, b) => _get(a, key, 0) - _get(b, key, 0),
  string: key => (a, b) => {
    const aName = _get(a, key, '').toLowerCase();
    const bName = _get(b, key, '').toLowerCase();
    if (aName > bName) return 1;
    if (aName < bName) return -1;
    return 0;
  }
};

const basicSorter = key => (a, b) => {
  if (a[key] < b[key]) return 1;
  if (a[key] > b[key]) return -1;
  return 0;
};

const file = {
  basename: (str, sep = '/') => str.substr(str.lastIndexOf(sep) + 1)
};

const s3ProxyUrl = (bucketName, filename) => {
  if (bucketName) return `${apiConfig.API_HOST}/s3/${bucketName}/${filename}`;
  return `${apiConfig.API_HOST}/s3/${filename}`;
};

const url = {
  query: param => {
    return _λflow(
      _λreplace('?', ''), // a=b454&c=dhjjh&f=g6hksdfjlksd
      _λsplit('&'), // ["a=b454","c=dhjjh","f=g6hksdfjlksd"]
      _λmap(keyValString => _split(keyValString, '=', 2)), // [["a","b454"],["c","dhjjh"],["f","g6hksdfjlksd"]]
      _λfromPairs // {"a":"b454","c":"dhjjh","f":"g6hksdfjlksd"}
    )(window.location.search)[param];
  },
  serialize: obj => {
    const str = [];
    for (let p in obj)
      if (obj.hasOwnProperty(p)) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
      }
    return str.join('&');
  }
};

// sums an array of floats represented as strings in a js-safe way and returns the sum as a string
// eg. ["100.00", "200.00", "300.00"] => "600.00"
export const safelySumFloatStrings = strings => {
  // sum using cents to avoid floating point imprecision
  const totalCents = strings.reduce((total, string) => {
    // parse the string as a float
    const value = parseFloat(string);

    // skip if the string is not a number
    if (isNaN(value)) return total;

    // multiply by 100 (to get cents) and round to handle any floating point issues
    return total + Math.round(value * 100);
  }, 0);

  // convert back to dollars and format to 2 decimals
  return (totalCents / 100).toFixed(2);
}

export const toNullableInt = (value) => {
  // treat null/undefined/'' as null; parse valid numeric strings to int
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  const num = parseInt(value, 10);
  return Number.isNaN(num) ? null : num;
};

export const isEmailAddressValid = emailAddress => {
  return emailValidator.validate(emailAddress);
}

export function getBase64ImageFromUrl(url) {
  return fetch(url)
    .then(response => response.blob())
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
}



/* ======================================== */
/*           US DOLLAR FORMATTING           */
/* ======================================== */

/**
 * @typedef {Object} FormatUSDOptions
 * @property {boolean} [cents=true] - when true, always show 2 decimal places; when false, show 0 decimals.
 */

/**
 * Formats a value as a USD currency string using Intl.NumberFormat.
 *
 * - Accepts numbers or numeric strings.
 * - Returns '' for null/undefined/empty-string or non-finite values.
 * - Locale-aware grouping and correct negative formatting (e.g. "-$1,234.56").
 *
 * @param {number|string|null|undefined} value
 * @param {FormatUSDOptions} [options]
 * @returns {string}
 */
export const formatUSD = (value, options = {}) => {
  const { cents = true } = options;

  if (value === null || typeof value === 'undefined') return '';
  if (value === '') return '';

  const num =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Number(value)
      : Number(value);

  if (!Number.isFinite(num)) return '';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    useGrouping: true,
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0
  }).format(num);
};

/**
 * Formats a user-typed value for a currency input without disrupting typing.
 *
 * Behavior goals:
 * - preserves partially typed decimals like "12." or "12.3"
 * - preserves "-" and "-12." shapes while still showing a "$" prefix
 * - once 2+ decimal digits are present, normalizes using formatUSD (2 decimals)
 *
 * Notes:
 * - returns a string intended for display in an input
 * - does not attempt to manage cursor position (that’s handled by the input component)
 *
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
export const formatUSDForInput = value => {
  if (value === null || typeof value === 'undefined') return '';

  const str = String(value);

  // allow clearing the input
  if (str === '') return '';

  // preserve bare minus while typing
  if (str === '-') return '-$';

  const isNegative = str.startsWith('-');
  const unsigned = isNegative ? str.slice(1) : str;
  const prefix = isNegative ? '-$' : '$';

  // allow partial typing states:
  // "12." => keep the dot
  // "12.3" => keep the single decimal digit
  // "0." / ".": keep as-is after prefix (".": user may be starting a decimal)
  const hasDot = unsigned.includes('.');
  const endsWithDot = unsigned.endsWith('.');
  const dotIndex = unsigned.indexOf('.');
  const decimalDigits =
    hasDot && dotIndex !== -1 ? unsigned.length - dotIndex - 1 : 0;

  if (unsigned === '.') return `${prefix}.`;
  if (endsWithDot) return `${prefix}${unsigned}`;
  if (hasDot && decimalDigits === 1) return `${prefix}${unsigned}`;

  // normalize once it's not a "typing shape" we want to preserve
  // If it has a dot, we force cents; otherwise still allow cents formatting (canonical)
  if (hasDot) return formatUSD(isNegative ? `-${unsigned}` : unsigned, { cents: true });

  // no dot yet: keep raw typing string (matches the original behavior)
  // e.g. "12" => "12" (not "$12.00") to avoid cursor/caret jump while typing
  return unsigned;
};

/**
 * Legacy: formats a number as currency with optional forced cents.
 *
 * @deprecated Use formatUSD(value, { cents: forceCents }) instead.
 * @param {number|string|null|undefined} value
 * @param {boolean} forceCents
 * @returns {string}
 */
export const renderCurrency = (value, forceCents) => {
  // original behavior returned '' for null/undefined
  if (typeof value === 'undefined' || value === null) return '';
  return formatUSD(value, { cents: !!forceCents });
};

/**
 * Legacy: formats an API string value as a dollar amount.
 *
 * Differences vs old implementation:
 * - now correct negative formatting (e.g. "-$14,099.04" not "$-14,099.04")
 * - locale-aware grouping
 * - returns '' for null/undefined/empty or non-numeric
 *
 * @deprecated Use formatUSD(value, { cents: true }) instead.
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
export const formatStringValueAsDollarAmount = value => {
  return formatUSD(value, { cents: true });
};

/**
 * Legacy: input-safe currency formatting.
 *
 * @deprecated Use formatUSDForInput(value) instead.
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
export const renderCurrencyForInput = value => {
  return formatUSDForInput(value);
};

/**
 * Formats a value as an abbreviated USD string (K/M/B/T), e.g.:
 * - 950        -> "$950"
 * - 1200       -> "$1.2K"
 * - 532000     -> "$532K"
 * - 14099000   -> "$14.1M"
 * - -14099.04  -> "-$14.1K"
 *
 * Notes:
 * - This is intentionally not Intl-based (suffixes are opinionated).
 * - For values < 1000, it returns "$" + the number without grouping (matches prior behavior).
 * - Uses up to 1 decimal place by default for abbreviated values, dropping trailing ".0".
 *
 * @param {number|string|null|undefined} value
 * @param {Object} [options]
 * @param {number} [options.decimals=1] - Max decimals for abbreviated values (0..3 recommended).
 * @param {number} [options.threshold=1000] - Minimum absolute value to abbreviate.
 * @param {boolean} [options.trimTrailingZero=true] - Drop trailing ".0" on abbreviated values.
 * @returns {string}
 */
export const formatUSDAbbr = (value, options = {}) => {
  const {
    decimals = 1,
    threshold = 1000,
    trimTrailingZero = true
  } = options;

  if (value === null || typeof value === 'undefined' || value === '') return '';

  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '';

  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);

  // preserve prior behavior of no grouping/decimals for < threshold
  if (abs < threshold) return `${sign}$${abs}`;

  // K, M, B, T (thousand, million, billion, trillion)
  const UNITS = [
    { value: 1e12, suffix: 'T' },
    { value: 1e9, suffix: 'B' },
    { value: 1e6, suffix: 'M' },
    { value: 1e3, suffix: 'K' }
  ];

  const unit = UNITS.find(u => abs >= u.value) || UNITS[UNITS.length - 1];
  const scaled = abs / unit.value;

  // If rounding would push us to 1000 of the current unit (e.g. 999.9K -> 1.0M),
  // bump to the next unit up.
  const roundedAtUnit = Number(scaled.toFixed(decimals));
  if (roundedAtUnit >= 1000) {
    const idx = UNITS.findIndex(u => u.value === unit.value);
    const higher = UNITS[idx - 1]; // because UNITS is descending
    if (higher) {
      const higherScaled = abs / higher.value;
      let s = higherScaled.toFixed(decimals);
      if (trimTrailingZero) s = s.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
      return `${sign}$${s}${higher.suffix}`;
    }
  }

  let out = scaled.toFixed(decimals);

  if (trimTrailingZero) {
    // "1.0" -> "1", "1.20" -> "1.2"
    out = out.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  }

  return `${sign}$${out}${unit.suffix}`;
};

/**
 * Legacy proxy: abbreviated currency formatter.
 *
 * @deprecated Use formatUSDAbbr(value) instead.
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export const renderAbbrCurrency = value => formatUSDAbbr(value);

export const parsePrice = (input => {
  // if the input is empty, return null 
  if (!input) return null;

  // if there are any non-numeric characters, other than those commonly found in dollar amounts, throw an error
  // [^0-9.,\-$\s] translates to "any character that is not a digit, comma, period, hyphen, dollar sign, or whitespace"
  if (/[^0-9.,\-$\s]/.test(input)) {
    throw new Error("There are invalid characters in the amount.");
  }

  // remove any non-numeric characters except for decimal point and minus sign
  const sanitizedInput = input.replace(/[^0-9.-]/g, '');
  
  // parse the sanitized input to a float
  const amount = parseFloat(sanitizedInput);

  // if the parsed amount is not a valid number, return null or handle the error
  if (isNaN(amount)) {
    throw new Error("The amount is not a number.");
  }

  // round to 2 decimal places
  const roundedAmount = Math.round(amount * 100) / 100;

  // convert to string and check total number of digits (excluding the decimal point)
  const [integerPart] = roundedAmount.toString().split('.');

  // ensure the integer part doesn't exceed 8 digits (max_digits - decimal_places)
  if (integerPart.length > 8) {
    throw new Error("The amount must be no more than 8 digits.");
  }

  // return the valid rounded amount
  return roundedAmount.toFixed(2); // Ensures it always returns 2 decimal places as a string
});



/* ======================================== */
/*               PERCENTAGES                */
/* ======================================== */

/**
 * parsePercentage
 * 
 * Parses a percentage string and returns a validated integer percentage.
 *
 * @param {string | null | undefined} input
 * @returns {number | null}
 * @throws {Error} If the input contains invalid characters, is not a number,
 * or is outside the range 0–100.
 */
export const parsePercentage = input => {
  // if the input is empty, return null
  if (!input) return null;

  // if there are any non-numeric characters, other than the percentage symbol, throw an error
  // [^0-9%] translates to "any character that is not a digit or a percentage symbol"
  if (/[^0-9%]/.test(input)) {
    throw new Error("There are invalid characters in the percentage.");
  }

  // remove the percentage symbol
  const sanitizedInput = input.replace('%', '');

  // parse the sanitized input to an integer
  const percentage = parseInt(sanitizedInput);

  // if the parsed percentage is not a valid number, return null or handle the error
  if (isNaN(percentage)) {
    throw new Error("The percentage is not a number.");
  }

  // ensure the percentage is within the valid range
  if (percentage < 0 || percentage > 100) {
    throw new Error("The percentage must be between 0 and 100.");
  }

  // return the valid percentage
  return percentage;
};

/**
 * formatPercentage
 * 
 * Formats a numeric value as a percentage string.
 *
 * @param {number | string | null | undefined} value
 * @returns {string}
 * @throws {Error} If the value cannot be parsed into a valid number.
 */
export const formatPercentage = value => {
  // if the input is empty, return an empty string
  if (typeof value === 'undefined' || value === null) return '';

  // ensure the value is a valid number
  const number = parseInt(value);

  if (isNaN(number)) {
    throw new Error("Invalid value for commission percentage");
  }

  // return the formatted string with the percentage symbol
  return `${number}%`;
};


/* ======================================== */
/*              DATES & TIMES               */
/* ======================================== */

/**
 * Date-only (YMD) handling notes:
 * - In some instances (maybe all?), like event dates, the server stores and sends "YYYY-MM-DD" with no time or timezone.
 * - In those cases, we essentially pretend the user is in UTC.
 * - This way, the date presented to the user matches the date on the server.
 * - If we were to do `new Date('YYYY-MM-DD')`, JS would interpret the date string as UTC midnight, 
 *   and then format in the user's local timezone — which can shift to the prior day in US timezones.
 *
 * This module treats YMD strings as "date-only" values and formats them using a stable
 * time zone (default: UTC) so "2025-11-21" always displays as November 21, 2025.
 * 
 * That said, we leave the door open for localization via options. 
 */

/**
 * @typedef {Object} YMDOptions
 * @property {'utc'|'local'} [source='utc'] - How to interpret the YMD string when creating a Date.
 * @property {'UTC'|string|undefined} [timeZone='UTC'] - Time zone to use for formatting. Use 'UTC'
 *   to keep date-only strings stable across user timezones.
 */

/**
 * Returns true if the input is a valid "YYYY-MM-DD" and corresponds to a real calendar date.
 *
 * @param {string|null|undefined} ymd
 * @returns {boolean}
 */
export const isValidYMD = ymd => {
  if (typeof ymd !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return false;

  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);

  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return false;
  if (mo < 1 || mo > 12) return false;

  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate(); // mo=1..12
  return d >= 1 && d <= daysInMonth;
};

/**
 * Parses a "YYYY-MM-DD" string into a JS Date at midnight.
 *
 * - source='utc' => Date at 00:00:00 UTC for that calendar day (recommended for server YMD).
 * - source='local' => Date at 00:00:00 in the user's local timezone (legacy behavior).
 *
 * @param {string} ymd - "YYYY-MM-DD"
 * @param {YMDOptions} [options]
 * @returns {Date}
 */
export const parseYMD = (ymd, options = {}) => {
  const { source = 'utc' } = options;

  // return invalid date if input is bad (mirrors Date constructor behavior)
  if (!isValidYMD(ymd)) return new Date(NaN);

  const [y, m, d] = ymd.split('-').map(Number);

  return source === 'local'
    ? new Date(y, m - 1, d) // local midnight
    : new Date(Date.UTC(y, m - 1, d)); // utc midnight
};

/**
 * Cached Intl.DateTimeFormat instances by key to avoid reallocation.
 * @type {Map<string, Intl.DateTimeFormat>}
 */
const _dtfCache = new Map();

const getDTF = (key, locale, options) => {
  const k = `${key}|${locale}|${JSON.stringify(options)}`;
  const existing = _dtfCache.get(k);
  if (existing) return existing;
  const fmt = new Intl.DateTimeFormat(locale, options);
  _dtfCache.set(k, fmt);
  return fmt;
};

/**
 * Formats a "YYYY-MM-DD" string to a long US date, e.g. "November 21, 2025".
 *
 * Defaults:
 * - source='utc' and timeZone='UTC' so the displayed day never shifts for users in
 *   negative offsets (e.g. America/Chicago).
 *
 * @param {string|null|undefined} ymd
 * @param {YMDOptions} [options]
 * @returns {string|null}
 */
export const formatYMDLong = (ymd, options = {}) => {
  if (!ymd) return null;

  const { source = 'utc', timeZone = 'UTC' } = options;
  const date = parseYMD(ymd, { source });
  if (Number.isNaN(date.getTime())) return null;

  const longDateFormatter = getDTF('long', 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone
  });

  return longDateFormatter.format(date);
};

/**
 * Formats a "YYYY-MM-DD" string to a short US date, e.g. "Nov 21, 2025".
 * (Handy replacement for `moment(dateYMD).format('MMM D, YYYY')`.)
 *
 * @param {string|null|undefined} ymd
 * @param {YMDOptions} [options]
 * @returns {string}
 */
export const formatYMDShort = (ymd, options = {}) => {
  if (!ymd) return '';

  const { source = 'utc', timeZone = 'UTC' } = options;
  const date = parseYMD(ymd, { source });
  if (Number.isNaN(date.getTime())) return '';

  const shortDateFormatter = getDTF('short', 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone
  });

  return shortDateFormatter.format(date);
};

/**
 * Formats a date range (YMD strings) into a compact long form:
 * - same month: "September 1-30, 2025"
 * - same year: "September 15 - October 3, 2025"
 * - different years: "December 30, 2025 - January 2, 2026"
 *
 * Defaults to stable UTC interpretation/formatting for server-supplied YMD.
 *
 * @param {string|null|undefined} startYMD
 * @param {string|null|undefined} endYMD
 * @param {YMDOptions} [options]
 * @returns {string|null}
 */
export const formatYMDRangeLong = (startYMD, endYMD, options = {}) => {
  if (!startYMD && !endYMD) return null;
  if (startYMD && !endYMD) return formatYMDLong(startYMD, options);
  if (!startYMD && endYMD) return formatYMDLong(endYMD, options);

  const { source = 'utc', timeZone = 'UTC' } = options;

  const start = parseYMD(startYMD, { source });
  const end = parseYMD(endYMD, { source });

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${startYMD} – ${endYMD}`;
  }

  // Use UTC getters if we're interpreting source as UTC; otherwise local getters.
  const getYear = source === 'utc' ? d => d.getUTCFullYear() : d => d.getFullYear();
  const getMonth = source === 'utc' ? d => d.getUTCMonth() : d => d.getMonth();
  const getDay = source === 'utc' ? d => d.getUTCDate() : d => d.getDate();

  const areSameYear = getYear(start) === getYear(end);
  const areSameMonth = areSameYear && getMonth(start) === getMonth(end);

  const monthFormatter = getDTF('monthLong', 'en-US', { month: 'long', timeZone });
  const startMonth = monthFormatter.format(start);
  const endMonth = monthFormatter.format(end);

  const startDay = getDay(start);
  const endDay = getDay(end);

  // same month: "September 1-30, 2025"
  if (areSameMonth) return `${startMonth} ${startDay}–${endDay}, ${getYear(start)}`;

  // same year: "September 15 - October 3, 2025"
  if (areSameYear) return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${getYear(start)}`;

  // different years: full long dates on both sides
  const startFull = formatYMDLong(startYMD, options);
  const endFull = formatYMDLong(endYMD, options);
  return `${startFull} – ${endFull}`;
};

/**
 * Formats seconds as M:SS.
 *
 * @param {number} seconds
 * @returns {string}
 */
export const formatSecondsAsMSS = seconds => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
};

/**
 * Counts how many calendar rows ("weeks") are needed to display a month in a typical
 * Sunday-start calendar grid.
 *
 * Input can be:
 * - a Date (any day within the month)
 * - a "YYYY-MM-DD" string (interpreted as UTC by default)
 *
 * @param {Date|string} monthStartDate
 * @param {Object} [options]
 * @param {'utc'|'local'} [options.source='utc'] - only used when monthStartDate is a YMD string
 * @returns {number}
 */
export const countWeeksInMonth = (monthStartDate, options = {}) => {
  const { source = 'utc' } = options;

  /** @type {Date} */
  const d =
    monthStartDate instanceof Date
      ? monthStartDate
      : parseYMD(String(monthStartDate), { source });

  if (Number.isNaN(d.getTime())) return 0;

  const y = source === 'utc' ? d.getUTCFullYear() : d.getFullYear();
  const m = source === 'utc' ? d.getUTCMonth() : d.getMonth();

  // day-of-week for the 1st of the month (0=Sun..6=Sat)
  const first = source === 'utc'
    ? new Date(Date.UTC(y, m, 1))
    : new Date(y, m, 1);

  const offset = source === 'utc' ? first.getUTCDay() : first.getDay();

  // number of days in month
  const daysInMonth = source === 'utc'
    ? new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
    : new Date(y, m + 1, 0).getDate();

  return Math.ceil((daysInMonth + offset) / 7);
};

/**
 * Two-letter weekday labels, Monday first. Use across calendar components
 * so all monthly grids stay aligned to the same week-start convention.
 */
export const DEFAULT_DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Number of empty cells before day 1 in a Monday-start month grid.
 * 0 when day-1 is Monday, 6 when day-1 is Sunday.
 * @param {moment.Moment} m
 * @returns {number}
 */
export const mondayLeadingOffset = m => (m.day() + 6) % 7;

/**
 * Number of empty cells after the last day in a Monday-start month grid.
 * 0 when last-day is Sunday, 6 when last-day is Monday.
 * @param {moment.Moment} m
 * @returns {number}
 */
export const mondayTrailingOffset = m => (13 - m.day()) % 7;

/**
 * Moment for 00:00 of the Monday at the start of the week containing m.
 * @param {moment.Moment} m
 * @returns {moment.Moment}
 */
export const startOfWeekMonday = m => m.clone().startOf('day').subtract(mondayLeadingOffset(m), 'days');

/**
 * Moment for end-of-day of the Sunday at the end of the week containing m.
 * @param {moment.Moment} m
 * @returns {moment.Moment}
 */
export const endOfWeekSunday = m => startOfWeekMonday(m).add(6, 'days').endOf('day');

/**
 * Returns a safe YMD string:
 * - if input is a valid YMD, returns it normalized (same string form)
 * - otherwise returns today's date as YMD in UTC (stable for server expectations)
 *
 * @param {string|null|undefined} maybeYMD
 * @returns {string}
 */
export const toSafeYMD = maybeYMD => {
  if (isValidYMD(maybeYMD)) return String(maybeYMD);

  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/* LEGACY PROXIES (keep old callsites working) */

/**
 * Returns a localized Date from a YMD string.
 * The server YMD is UTC, so prefer parseYMD(ymd, { source: 'utc' }).
 *
 * @deprecated Use parseYMD(ymd, { source: 'local' }) for the same behavior, or parseYMD(..., { source: 'utc' }).
 * @param {string} ymd
 * @returns {Date}
 */
export const parseLocalYMD = ymd => parseYMD(ymd, { source: 'local' });

/**
 * Legacy: "YYYY-MM-DD" -> "November 21, 2025"
 *
 * @deprecated Use formatYMDLong(dateStr) (defaults to UTC-stable formatting).
 * @param {string} dateStr
 * @returns {string|null}
 */
export const formatNumericalDateToLongString = dateStr => formatYMDLong(dateStr);

/**
 * Legacy: date range formatting.
 *
 * @deprecated Use formatYMDRangeLong(startDateStr, endDateStr).
 * @param {string|null|undefined} startDateStr
 * @param {string|null|undefined} endDateStr
 * @returns {string|null}
 */
export const formatDateRangeToLongString = (startDateStr, endDateStr) =>
  formatYMDRangeLong(startDateStr, endDateStr);

/**
 * Legacy: weeks-in-month naming.
 *
 * @deprecated Use countWeeksInMonth(monthStartDate).
 * @param {Date|string} monthStartDate
 * @returns {number}
 */
export const getWeeksInMonth = monthStartDate => countWeeksInMonth(monthStartDate);

/**
 * Legacy: moment-based renderDate. Replaced with Intl-based formatting.
 *
 * @deprecated Use formatYMDShort(dateYMD) (defaults to UTC-stable formatting).
 * @param {string|null|undefined} dateYMD
 * @returns {string}
 */
export const renderDate = dateYMD => formatYMDShort(dateYMD);

/**
 * Legacy: Moment adapter.
 *
 * @param {string|Date|number|null|undefined} value
 * @returns {any} moment.Moment
 */
export const toMoment = value => {
  // supports ISO strings, JS Dates, ms timestamps, unix seconds
  if (typeof value === 'number') {
    return value < 1000000000000 ? moment.unix(value) : moment(value);
  }
  return moment(value);
};



/* ======================================== */
/*                JSON API                  */
/* ======================================== */

const attachIncludesToResource = (resource, state) => {
  const resourceWithAttachedIncludes = Object.assign({}, resource);

  if (_get(resource, 'relationships')) {
    _each(resource.relationships, (value, key) => {
      if (!_isArray(value.data) && _get(value, 'data.id')) {
        const type = pluralizeKey(_snakeCase(_get(value, 'data.type')));
        const include = _get(state, `resources.${type}[${value.data.id}]`);
        resourceWithAttachedIncludes[key] = attachIncludesToResource(include, state);
      } else if (_isArray(value.data)) {
        const ids = _λflow(_λmap(reference => _get(reference, 'id')), _λcompact)(value.data);
        const type = pluralizeKey(_snakeCase(_get(value, 'data[0].type')));
        const includes = _pick(_get(state, `resources.${type}`, {}), ids);

        resourceWithAttachedIncludes[key] = includes;
      }
    });
  }

  return resourceWithAttachedIncludes;
};

const resourceArrayToObject = resources => {
  return _reduce(
    resources,
    (memo, resource) => {
      memo[resource.id] = resource;
      return memo;
    },
    {}
  );
};

const storeIncludes = (state, resources) => {
  const resourcesByType = _groupBy(resources, resource => pluralizeKey(_snakeCase(resource.type)));
  const newState = Object.assign({}, state);

  _each(resourcesByType, (value, key) => {
    newState[key] = Object.assign({}, _get(newState, key, {}), resourceArrayToObject(value));
  });

  return newState;
};

const pluralizeKey = key => {
  if (key.match(/s$/)) {
    return key;
  } else if (key.match(/y$/)) {
    return `${key.substring(0, key.length - 1)}ies`;
  } else {
    return `${key}s`;
  }
};

export function buildQuery(resourceType, id = null, { action, fields = {}, filters = {}, include, page, path, pageSize, prefix, sort } = {}) {
  const data = {};
  if (include) data.include = include.join(',');
  if (page) data.page = page;
  if (pageSize) data.page_size = pageSize;
  if (sort) data.sort = sort;

  _map(filters, (value, key) => (data[`filter[${key}]`] = value));

  _map(fields, (value, key) => (data[`fields[${key}]`] = value.join(',')));

  const requestPath = path || _compact([prefix, resourceType, id, action]).join('/');
  const params = _toPairs(data).map(pair => pair.join('=')).join('&');

  return `${apiConfig.API_HOST}/${requestPath}/${params ? '?' + params : ''}`; // e.g. http://localhost:3001/properties/?page_size=1000&filter[active]=true
};

export const getFirstApiErrorMessage = payload => {
  if (!payload) return null;

  // json:api errors: { errors: [{ detail, title, ... }] }
  const jsonApiDetail = payload?.errors?.[0]?.detail
    ?? payload?.errors?.[0]?.title;
  if (typeof jsonApiDetail === 'string' && jsonApiDetail.trim()) {
    return jsonApiDetail;
  }

  // drf: { field: ["msg"] } or { detail: "msg" } or { non_field_errors: [...] }
  if (typeof payload?.detail === 'string' && payload.detail.trim()) {
    return payload.detail;
  }

  const entries = Object.entries(payload);
  if (!entries.length) return null;

  const [, value] = entries[0];

  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'string' ? first : null;
  }

  if (typeof value === 'string') return value;

  // sometimes nested, e.g. { field: { subfield: ["msg"] } }
  if (value && typeof value === 'object') {
    return getFirstApiErrorMessage(value);
  }

  return null;
}

/* ======================================== */
/*            REDUX PROJECTORS              */
/* ======================================== */

const isLoggedIn = state => _get(state, 'auth.token') !== undefined;

const isPhone = state => _get(state, 'device.type') === 'PHONE';

const isStaff = state => _get(state, 'auth.me.auth_user.attributes.is_staff') === true;

const isCreator = state => _get(state, 'auth.me.type') === 'User' && _get(state, 'auth.me.auth_user.attributes.is_staff') === false;

const isReadOnlyCreator = state => _get(state, 'auth.me.attributes.read_only') === true;

const isSponsor = state => _get(state, 'auth.me.type') === 'SponsorUser';

const isFetchingCurrentUser = state => _get(state, 'auth.isFetchingCurrentUser') === true;


/* ======================================== */
/*               UI HELPERS                 */
/* ======================================== */

/**
 * Generates a deterministic hex color code from an input string.
 *
 * The same input string will always produce the same color. This is useful for
 * assigning stable colors to usernames, IDs, tags, or other identifiers.
 *
 * Two color-generation modes are supported:
 *
 * 1. RGB hash mode (`darkColorsOnly = false`)
 *    - Uses the hashed value directly to derive RGB components.
 *    - Produces a wide range of colors, including light and bright ones.
 *
 * 2. Dark HSL mode (`darkColorsOnly = true`, default)
 *    - Derives a hue from the hash and uses fixed saturation/lightness.
 *    - Produces visually consistent, moderately dark colors that work well
 *      on light backgrounds and avoid near-white pastels.
 *
 * Notes:
 * - The function is deterministic but not cryptographically secure.
 * - Small changes in the input string will typically produce different colors.
 * - Returned color is always a 6-digit uppercase hex string (e.g. "#3A5F9C").
 *
 * @param {string} [str=''] - Input string to hash into a color.
 * @param {boolean} [darkColorsOnly=true] - When true, generates darker,
 *   HSL-based colors; when false, uses direct RGB hashing.
 * @returns {string} A hex color string in the form "#RRGGBB".
 */
const hashColorFromString = (str = '', darkColorsOnly = true) => {
  let hash = 0;
  
  // generate hash from the input string
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  if (!darkColorsOnly) {
    // original RGB-based color generation
    hash = hash & 0x00ffffff;
    let r = (hash >> 16) & 0xFF;
    let g = (hash >> 8) & 0xFF;
    let b = hash & 0xFF;
    
    const toHex = (c) => {
      const hex = c.toString(16).toUpperCase();
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } else {
    // hsl-based dark color generation
    const hue = Math.abs(hash) % 360; // this ensures the hue is within [0, 360)
    
    const saturation = 60; // percentage
    const lightness = 50;  // percentage (lower value for darker colors)
    
    // convert hsl to hex color
    const hslToHex = (h, s, l) => {
      s /= 100;
      l /= 100;
      
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs((h / 60) % 2 - 1));
      const m = l - c / 2;
      let r, g, b;
      
      if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
      else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
      else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
      else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
      else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
      else { r = c; g = 0; b = x; }
      
      r = Math.round((r + m) * 255);
      g = Math.round((g + m) * 255);
      b = Math.round((b + m) * 255);
      
      const toHex = (c) => {
        const hex = c.toString(16).toUpperCase();
        return hex.length === 1 ? '0' + hex : hex;
      };
      
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };
    
    return hslToHex(hue, saturation, lightness);
  }
};

const viewportSize = () => ({
  width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
  height: Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
});

const viewportMobile = () => viewportSize().width <= 540;

const getIconForUrl = (url, size = 32) =>
  `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=${size}`;


/* ======================================== */
/*            APP-LIKE HELPERS              */
/* ======================================== */

const isProduction = () => {
  return window.location.hostname === 'hello.standard.tv';
};

export const redirect = (event, history, url) => {
  if (event.metaKey === true) {
    window.open(url, '_blank');
  } else {
    history.push(url);
  }
};

const createAndAppendIconLink = (type, size) => {
  const folder = type === 'icon' ? 'transparent' : 'square';
  const link = document.createElement('link');
  link.rel = type;
  link.sizes = `${size}x${size}`;
  link.href = `/icons/${process.env.NODE_ENV}/${folder}/${size}x${size}.png`;
  if (type === 'icon') link.type = 'image.png';
  document.head.appendChild(link);
};

export const copyToClipboard = async text => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // fallback for older browsers / non-HTTPS
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed'; // avoid scroll jump
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  } catch (err) {
    console.error('failed to copy to clipboard:', err);
  }
};


/* ======================================== */
/*               GOOGLE API                 */
/* ======================================== */

const gapi = window.gapi;
const isDevEnv = process.env.NODE_ENV === 'development' ? true : false;
const loadClientWhenGapiReady = script => {
  if (isDevEnv) console.log('Trying To Load Client!');
  if (isDevEnv) console.log(script);
  if (script.getAttribute('gapi_processed')) {
    if (isDevEnv) console.log('Client is ready! Now you can access gapi. :)');
    if (window.location.hostname === 'localhost') {
      gapi.client.load('http://localhost:8080/_ah/api/discovery/v1/apis/metafields/v1/rest').then(
        response => {
          if (isDevEnv) console.log('Connected to metafields API locally.');
        },
        function(err) {
          if (isDevEnv) console.log('Error connecting to metafields API locally.');
        }
      );
    }
  } else {
    if (isDevEnv) console.log("Client wasn't ready, trying again in 100ms");
    setTimeout(() => {
      loadClientWhenGapiReady(script);
    }, 100);
  }
};

const initGapi = () => {
  if (isDevEnv) console.log('Initializing GAPI...');
  if (isDevEnv) console.log('Creating the google script tag...');

  const script = document.createElement('script');
  script.onload = () => {
    if (isDevEnv) console.log('Loaded script, now loading our api...');
    // Gapi isn't available immediately so we have to wait until it is to use gapi.
    loadClientWhenGapiReady(script);
  };
  script.src = 'https://apis.google.com/js/api.js';

  document.body.appendChild(script);
};


/* ======================================== */
/*                 EVENTS                   */
/* ======================================== */

export function groupEventsByMonth(events, isStaff) {
  const flattenedEvents = _flatMapDeep(events, event => {
    const filteredSponsorships = _filter(event.sponsorships, sponsorship => !!isStaff || !!checkShouldSponsorshipBeVisibleToCreator(sponsorship));
    if (!_isEmpty(filteredSponsorships)) {
      let showDate = true;

      return _map(filteredSponsorships, sponsorship => {
        let name = _get(sponsorship, 'sponsor.name', <TextMuted>Placeholder</TextMuted>);
        const isDraft = checkIsSponsorshipADraft(sponsorship);
        if (isDraft) {
          name += ' (Draft)';
        } else if (sponsorship.placeholder) {
          name += ' (Deal in progress)';
        }
        const data = {
          ...event,
          ...sponsorship,
          ...sponsorship.sponsor,
          propertyName: _get(event, 'property.name', ''),
          name,
          id: sponsorship.id,
          eventId: event.id,
          key: 'sponsorship-' + sponsorship.id,
          displayDate: showDate ? event.date : '',
          price: parseFloat(sponsorship.price),
          sponsored: true
        };

        showDate = false;
        return data;
      });
    } else {
      return {
        id: event.id,
        eventId: event.id,
        key: 'event-' + event.id,
        propertyName: _get(event, 'property.name', ''),
        displayDate: event.date,
        sponsored: false,
        ...event
      };
    }
  });

  const sortedAndGroupedEvents = _λflow(_λsortBy('date'), _λgroupBy(event => moment(event.date).format('YYYY-MM-01')))(flattenedEvents);
  return sortedAndGroupedEvents;
};


/* ======================================== */
/*              SPONSORSHIPS                */
/* ======================================== */

const getCopyVarsFromSponsorhip = sponsorshipWithSponsor => {
  let copyPerShowVarsJson = _get(sponsorshipWithSponsor, `sponsor.attributes.copy_per_show_vars`);
  if (copyPerShowVarsJson === undefined || copyPerShowVarsJson === '') {
    copyPerShowVarsJson = '{}';
  }

  const copyPerShowVars = JSON.parse(copyPerShowVarsJson);
  const vars = _get(copyPerShowVars, _get(sponsorshipWithSponsor, 'event.property.id'), {});

  vars.url = _get(sponsorshipWithSponsor, 'attributes.custom_link_url') || vars.url || _get(sponsorshipWithSponsor, 'sponsor.attributes.copy_link_url') || '';

  vars.code = _get(sponsorshipWithSponsor, 'attributes.custom_code') || vars.code || '';

  return vars;
};

export const checkIsSponsorshipADraft = sponsorship => {
  const insertionOrder = _get(sponsorship, 'insertion_order', null);
  if (insertionOrder) {
    const insertionOrderDate = insertionOrder.created_at;
    const createdAfterDec12018 = moment(new Date(insertionOrderDate)).isAfter(new Date('2018-12-01'));
    const wasSentToSponsor = !!_get(sponsorship, 'insertion_order.sent_to_sponsor_at', false);
    const isSigned = !!_get(sponsorship, 'insertion_order.signed_at', false);
    return !!createdAfterDec12018 && !wasSentToSponsor && !isSigned;
  } else {
    return false;
  }
};

export const checkShouldSponsorshipBeVisibleToCreator = sponsorship => {
  const insertionOrder = _get(sponsorship, 'insertion_order', null);
  if (insertionOrder) {
    const insertionOrderDate = insertionOrder.created_at;
    const createdAfterDec12018 = moment(new Date(insertionOrderDate)).isAfter(new Date('2018-12-01'));
    const wasSentToSponsor = !!_get(sponsorship, 'insertion_order.sent_to_sponsor_at', false);
    const isSigned = !!_get(sponsorship, 'insertion_order.signed_at', false);
    return !!createdAfterDec12018 && (wasSentToSponsor || isSigned);
  } else {
    return false;
  }
};


/* ======================================== */
/*           SPONSORSHIP BLOCKS             */
/* ======================================== */

// eg "Sponsorship Block (LowSpecGamer)" etc.
function generateBlockTitle(block) {
  return block?.property?.name ?? '[Unknown]';
}

// eg "Sponsorship Block (3-5 sponsorships)" etc.
function generateBlockSubtitle(block) {
  const startDate = block?.start_date;
  const endDate = block?.end_date ?? null;
  const dateRange = startDate && endDate ? formatYMDRangeLong(startDate, endDate) : 'Failed to parse date range';
  const min = block.minimum_sponsorships;
  const max = block.maximum_sponsorships;

  let rangeText;

  if (min && max && min === max) {
    rangeText = `${min} sponsorship${min === 1 ? '' : 's'}`;
  } else if (min && max) {
    rangeText = `${min}-${max} sponsorships`;
  } else if (min && !max) {
    rangeText = `min ${min} sponsorships`;
  } else if (!min && max) {
    rangeText = `up to ${max} sponsorships`;
  } else {
    rangeText = '';
  }

  return `${rangeText} from ${dateRange}`;
}



/* ======================================== */
/*            INSERTION ORDERS              */
/* ======================================== */

export function sortLineItemsByDate(items) {
  if (!Array.isArray(items)) return [];

  return [...items].sort((a, b) => {
    const aDate = a.date || a.startDate;
    const bDate = b.date || b.startDate;

    // both null -> keep original order
    if (!aDate && !bDate) return 0;

    // nulls go last
    if (!aDate) return 1;
    if (!bDate) return -1;

    // compare actual dates
    return new Date(aDate) - new Date(bDate);
  });
}

export function normalizeSponsorshipToLineItem(s) {
  if (!s) return null;

  const date = s?.event?.date || null;
  const commissionPercentage = s?.attributes?.commission_percentage ?? s?.commission_percentage ?? null;

  return {
    id: `spon-${s.id}`,
    type: 'sponsorship',
    event: s?.event ?? null,
    property: s?.event?.property ?? null,
    sponsor: s?.event?.sponsor ?? null,
    date,
    dateString: date ? formatYMDLong(date) : null, // "September 3, 2025"
    title: `${s?.event?.property?.name}` || '[Unknown Property]',
    campaign: s?.campaign || null,
    sponsorLink: s.sponsor_url || null,
    commissionPercentage: commissionPercentage != null
      ? Number(commissionPercentage)
      : null,
    price: Number(s?.price ?? s?.event?.property?.default_sponsorship_price ?? null),
    copy: s?.copy ?? s?.sponsor?.copy ?? '',
    internalNotes: s?.internal_notes ?? '',
  };
}

export function normalizeSponsorshipBlockToLineItem(b) {
  if (!b) return null;

  const date = b?.start_date || null;
  const commissionPercentage = b?.commission_percentage ?? null;

  return {
    id: `bloc-${b.id}`,
    type: 'sponsorship_block',
    property: b?.property ?? null,
    date,
    dateString: date ? formatYMDLong(date) : null, // "September 3, 2025"
    title: generateBlockTitle(b),
    subtitle: generateBlockSubtitle(b),
    campaign: b?.campaign ?? null,
    sponsorLink: b.sponsor_url ?? null,
    startDate: b?.start_date,
    endDate: b?.end_date,
    commissionPercentage: commissionPercentage != null
      ? Number(commissionPercentage)
      : null,
    price: b?.price != null
      ? Number(b?.price)
      : null,
    minimumSponsorships: b.minimum_sponsorships ?? null,
    maximumSponsorships: b.maximum_sponsorships ?? null,
    copy: b?.copy ?? '',
    internalNotes: b?.internal_notes ?? '',
  };
}

// insertionOrder -> lineItems
export function normalizeInsertionOrderToLineItems(insertionOrder) {
  if (!insertionOrder) return [];

  const sponsorshipItems = (insertionOrder.sponsorships || []).map(normalizeSponsorshipToLineItem);

  const blockItems = (insertionOrder.sponsorship_blocks || []).map(normalizeSponsorshipBlockToLineItem);

  return sortLineItemsByDate(sponsorshipItems.concat(blockItems));
}

export function getYouTubeVideoId(url) {
  const match = url.match(
    /(?:youtube\.com.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/
  );
  return match ? match[1] : null;
}

export function getYoutubeThumbnail(youtubeId, quality = 'max') {
  const map = {
    default: 'default.jpg',
    medium: 'mqdefault.jpg',
    high: 'hqdefault.jpg',
    standard: 'sddefault.jpg',
    max: 'maxresdefault.jpg',
  };

  return `https://img.youtube.com/vi/${youtubeId}/${map[quality]}`;
};

// minimal trailing-edge debounce with a cancel method (pure-js replacement for lodash debounce)
export const debounce = (fn, wait) => {
  let timer = null;
  const debounced = (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, wait);
  };
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return debounced;
};


/* ======================================== */
/*                 EXPORT                   */
/* ======================================== */

export default {
  attachIncludesToResource,
  basicSorter,
  checkIsSponsorshipADraft,
  createAndAppendIconLink,
  debounce,
  file,
  formatPercentage,
  formatSecondsAsMSS,
  formatStringValueAsDollarAmount,
  getBase64ImageFromUrl,
  getCopyVarsFromSponsorhip,
  getFirstApiErrorMessage,
  getIconForUrl,
  getWeeksInMonth,
  hashColorFromString,
  initGapi,
  isCreator,
  isEmailAddressValid,
  isFetchingCurrentUser,
  isLoggedIn,
  isPhone,
  isProduction,
  isReadOnlyCreator,
  isSponsor,
  isStaff,
  loadClientWhenGapiReady,
  normalizeInsertionOrderToLineItems,
  parseLocalYMD,
  parsePercentage,
  pluralizeKey,
  renderAbbrCurrency,
  renderCurrency,
  renderCurrencyForInput,
  resourceArrayToObject,
  s3ProxyUrl,
  safelySumFloatStrings,
  sorter,
  storeIncludes,
  url,
  viewportMobile,
  viewportSize
};
